import classNames from 'classnames';
import React from 'react';

import {useI18n} from '@udemy/i18n';
import {Link} from '@udemy/react-core-components';
import {ProgressRing} from '@udemy/react-messaging-components';

import trackerStyles from 'src/components/grow-with-google/google-tracker/google-tracker.module.less';

import styles from './hero-progress-card.module.less';

export interface HeroProgressCardProps {
    totalCourses: number;
    completedCourses: number;
    nextCourseUrl?: string;
    nextCourseNumber?: number;
    /** Progress of the next course (0-100). >0 means started/in-progress — show "Resume" instead of "Start" */
    nextCourseProgress?: number;
}

export const HeroProgressCard: React.FC<HeroProgressCardProps> = ({
    totalCourses,
    completedCourses,
    nextCourseUrl,
    nextCourseNumber,
    nextCourseProgress = 0,
}) => {
    const {gettext, interpolate} = useI18n();

    // Check if the next course has progress (>0 means started/in-progress, =0 means not started)
    const hasProgress = nextCourseProgress > 0;

    // Progress text
    const progressCountText = interpolate(
        gettext('%(completed)s of %(total)s'),
        {
            completed: completedCourses,
            total: totalCourses,
        },
        true,
    );
    const progressLabelText = gettext('courses completed');

    const getCourseCta = () => {
        if (!nextCourseNumber) return null;

        if (hasProgress) {
            return interpolate(
                gettext('Resume course %(courseNumber)s of %(total)s'),
                {courseNumber: nextCourseNumber, total: totalCourses},
                true,
            );
        }
        return interpolate(
            gettext('Start course %(courseNumber)s of %(total)s'),
            {courseNumber: nextCourseNumber, total: totalCourses},
            true,
        );
    };

    return (
        <div className={styles['progress-wrapper']}>
            <h3 className="ud-heading-md ud-text-bold">{gettext('Your certificate progress')}</h3>
            <div className={styles['progress-ring-container']}>
                <ProgressRing
                    label={gettext('Your certificate progress')}
                    max={totalCourses}
                    value={completedCourses}
                />
                <div className={styles['progress-text']}>
                    <span className={trackerStyles['progress-count']}>{progressCountText}</span>
                    <span className={trackerStyles['progress-label']}>{progressLabelText}</span>
                </div>
            </div>
            {nextCourseUrl && (
                <div className={styles['cta-container']}>
                    <Link
                        to={nextCourseUrl}
                        className={classNames(
                            'ud-btn',
                            'ud-btn-large',
                            'ud-btn-primary',
                            styles['cta-button'],
                        )}
                    >
                        {getCourseCta()}
                    </Link>
                </div>
            )}
        </div>
    );
};
